import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from '@/types';

import { customBaseQuery } from '@/lib/baseQuery';
import { mapObjectKeysToSnakeCase } from '@/utils/mapper';

/**
 * Submit Annual Tax Assessment
 *
 * Endpoint: /api/tax/v1/annual-assessment/
 * Method:   POST
 * Purpose:  Creates the annual tax assessment, linking one or more property IDs
 *           returned from the property creation/list endpoint.
 *
 * Payload structure:
 * {
 *   "details": { ... assessment-level fields (camelCase from form, snake_cased here) ... },
 *   "properties": [124, 125, 126]
 * }
 */
type SubmitAnnualAssessmentPayload = {
  details: Record<string, any>;
  properties: number[];
};

export const annualAssessmentAPI = createApi({
  reducerPath: 'annualAssessmentAPI',
  baseQuery: customBaseQuery,
  tagTypes: ['AnnualAssessment'],
  endpoints: (builder) => ({
    submitAnnualAssessment: builder.mutation<
      ApiResponse<any>,
      SubmitAnnualAssessmentPayload
    >({
      query: ({ details, properties }) => {
        const transformedDetails = mapObjectKeysToSnakeCase(details);
        return {
          url: 'api/tax/v1/annual-assessment/',
          method: 'POST',
          body: {
            details: transformedDetails,
            properties,
          },
        };
      },
      invalidatesTags: ['AnnualAssessment'],
    }),
    getAnnualAssessments: builder.query<ApiResponse<any[]>, void>({
      query: () => ({
        url: 'api/tax/v1/annual-assessment/list/',
        method: 'GET',
      }),
      providesTags: ['AnnualAssessment'],
    }),
  }),
});

export const {
  useSubmitAnnualAssessmentMutation,
  useGetAnnualAssessmentsQuery,
} = annualAssessmentAPI;
